import app from 'flarum/forum/app';

type Autogrant = { ok: boolean; code?: string; inviter?: string | null; source?: string } | null;

/**
 * Cuenta, una sola vez, como acabo la invitacion que se canjeo sola al confirmar el correo.
 *
 * --- POR QUE ESTO EXISTE ---
 *
 * Quien crea la cuenta desde un enlace de invitacion ya no tiene que volver al enlace: el
 * servidor termina el canje en cuanto el correo queda confirmado. Pero un cambio de estado que
 * nadie anuncia es indistinguible de que no haya pasado nada, y ademas el canje PUEDE fallar
 * —invitacion caducada, agotada, retirada—. El paquete lo dice literal: error claro, no silencio.
 *
 * `null` en el payload es el caso corriente (no habia nada pendiente) y no pinta nada.
 *
 * Mismo patron one-shot que `showActivationToast`: se borra del payload local para que una
 * inicializacion defensiva repetida en la misma pagina no duplique el aviso.
 */
export default function showInviteAutograntToast(): void {
  const result = (app.data as any).umbralInviteAutogrant as Autogrant;

  if (!result || typeof result !== 'object') return;

  (app.data as any).umbralInviteAutogrant = null;

  const t = (key: string, params?: any) => app.translator.trans('umbral-comunidad.forum.invitacion.' + key, params);

  if (result.ok) {
    app.alerts.show(
      { type: 'success', title: t('autogrant_title') },
      result.inviter ? t('autogrant_body_by', { name: result.inviter }) : t('autogrant_body')
    );

    // La sesion pertenece a un grupo mas que hace un segundo. Sin recargar el usuario, el resto
    // de la SPA (la tarjeta de n8n, las categorias visibles) seguiria creyendo que no.
    if (app.session.user) {
      app.store.find('users', app.session.user.id()!).catch(() => {});
    }

    return;
  }

  // El codigo viene del dominio y es el mismo que pinta la pagina del enlace, asi que el motivo
  // ya esta traducido; si llega uno que esta pantalla no sabe explicar, se dice lo generico en
  // vez de ensenar un identificador interno.
  const known = ['not_found', 'revoked', 'expired', 'exhausted', 'already_member', 'self', 'unconfirmed', 'not_started', 'paused', 'access_degraded'];
  const code = result.code && known.includes(result.code) ? result.code : null;

  // «Ya eres miembro» NO es un fallo: es el final feliz por otra puerta —alguien ya estaba
  // dentro cuando confirmó—. Pintarlo con el título de error diría lo contrario de su propio
  // cuerpo, que es exactamente la clase de mensaje contradictorio que este paquete viene a
  // quitar del camino.
  if (code === 'already_member') {
    app.alerts.show({ type: 'success', title: t('autogrant_title') }, t('error_already_member'));

    return;
  }

  app.alerts.show({ type: 'error', title: t('autogrant_failed_title') }, code ? t('error_' + code) : t('autogrant_failed_body'));
}
